import React, { useState, useEffect, useRef } from "react"
import {
  Box,
  IconButton,
  Chip,
  MenuItem,
  Divider,
  Popover,
  useTheme,
  ListItemIcon,
  ListItemText,
  Hidden,
  useMediaQuery,
  Button,
  Tooltip,
} from "@material-ui/core"
import { Link, NavLink, useHistory, useLocation } from "react-router-dom"
import { makeStyles } from "@material-ui/styles"
import { Error as ErrorIcon, Tune as TuneIcon, ExitToApp as ExitToAppIcon, SwapHoriz } from "@material-ui/icons"
import Config from "react-global-configuration"
import { useApolloClient } from "@apollo/client"
import moment from "moment"
import { Icon, SearchPopover, Avatar, NotificationIndicator, LocationSwitcher, AnnouncementButton } from ".."
import { useAuth } from "../../services"
import { useSnackbar } from "../SnackbarProvider"
import { RequirePermissions } from "../Permissions"
import { IdleTimer } from "./IdleTimer"
import { RowBox } from "../Boxes"
import { HelpMenu } from "./HelpMenu"
import { truncateInMiddle, useDateUtils } from "../../utils"

const useStyles = makeStyles((theme) => ({
  tray: {
    display: "flex",
    flexDirection: "row",
    marginLeft: "auto",
    alignItems: "center",
  },
  trayItem: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  trayOrganisation: {
    fontSize: 13,
    marginRight: 11,
    backgroundColor: theme.palette.shaded.main,
    "&:hover": {
      background: "#e6e6e6",
    },
    "&:focus": {
      background: "#e6e6e6",
    },
  },
  clickable: {
    cursor: "pointer",
  },
  organisationLogo: {
    width: 24,
    height: 24,
  },
  trayOrganisationLabel: {
    padding: ({ smDown }) => (smDown ? "0 2px" : "0 12px"),
  },
  trayIconButton: {
    color: "black",
    marginRight: 0,
    padding: theme.spacing(1),
  },
  trayAvatarButton: {
    marginRight: theme.spacing(0),
    padding: "4px",
  },
  trayAvatar: {
    width: 40,
    height: 40,
  },
  time: {
    color: theme.palette.text.light,
    marginLeft: theme.spacing(0.5),
  },
  locationPrimary: {
    lineHeight: "16px",
  },
  menu: {
    minWidth: 307,
  },
  menuItem: {
    "&:hover": {
      background: theme.palette.primary.shaded.background,
    },
  },
  menuItemShaded: {
    backgroundColor: theme.palette.shaded.main,
    "&:hover": {
      background: theme.palette.shaded.main,
    },
  },
  menuUser: {
    fontSize: 16,
    lineHeight: "16px",
    fontWeight: "600",
    color: theme.palette.text.tinted,
  },
  menuLight: {
    fontSize: 12,
    lineHeight: "16px",
    color: theme.palette.text.light,
  },
  menuListItemIcon: {
    minWidth: "auto",
    paddingRight: theme.spacing(2),
  },
  kioskTimeLeft: {
    border: `1px solid ${theme.palette.secondary.main}`,
    borderRadius: "4px 0 0 4px",
    height: 36,
    width: 64,
    fontSize: 16,
    fontWeight: "600",
    color: theme.palette.secondary.main,
    textAlign: "center",
    lineHeight: "34px",
  },
  kioskLogout: {
    borderRadius: "0 4px 4px 0",
  },
}))

const HeaderTray = ({ onKioskLoggingOut }) => {
  const theme = useTheme()
  const history = useHistory()
  const xs = useMediaQuery(theme.breakpoints.down("xs"))
  const smDown = useMediaQuery(theme.breakpoints.down("sm"))
  const classes = useStyles({ smDown })
  const { hasTimeOffsetFromUser } = useDateUtils()
  const { clientKiosk: kiosk, clientDevice: device } = Config.get()
  const client = useApolloClient()
  const {
    authed,
    principal,
    settings: { organisation, locations },
    location,
    logout,
    hasFeature,
  } = useAuth(client)
  const { pathname } = useLocation()
  const searchButtonEl = useRef(null)
  const [searchEl, setSearchEl] = useState(null)
  const accountButtonEl = useRef(null)
  const [accountEl, setAccountEl] = useState(null)
  const [showLocationSwitcher, setShowLocationSwitcher] = useState(false)
  const [global, setGlobal] = useState(false)
  const { showMessage, clearMessage } = useSnackbar()

  useEffect(() => {
    if (!location)
      showMessage({
        color: "secondary",
        message: (
          <>
            There are no locations associated {smDown && <br />}with your user. Contact your {smDown && <br />}system
            administrator.
          </>
        ),
        icon: <ErrorIcon />,
        keepOpen: true,
      })
    return () => clearMessage()
  })

  useEffect(() => {
    setGlobal(
      pathname.startsWith("/knowledge") ||
        pathname.startsWith("/hub") ||
        pathname.startsWith("/people") ||
        pathname.startsWith("/reports") ||
        pathname.startsWith("/templates") ||
        pathname.startsWith("/training") ||
        pathname.startsWith("/account")
    )
  }, [pathname, setGlobal])

  const isAuthed = authed
  if (!isAuthed) return null

  const { username, firstName, lastName, email, avatar, external } = principal

  const handleSearchClick = (event) => {
    setSearchEl(event.currentTarget)
  }
  const handleSearchClose = () => {
    setSearchEl(null)
  }

  const handleAccountClick = (event) => {
    setAccountEl(event.currentTarget)
  }
  const handleAccountClose = () => {
    setAccountEl(null)
  }

  const handleLocationClick = () => {
    setShowLocationSwitcher(true)
    handleAccountClose()
  }
  const handleLocationClose = () => {
    setShowLocationSwitcher(false)
  }

  const handleLogout = async () => {
    await logout()
    history.push("/login")
  }

  const handleKioskLogout = () => {
    onKioskLoggingOut && onKioskLoggingOut()
    logout()
  }

  const handleIdleLoggingOut = () => {
    onKioskLoggingOut && onKioskLoggingOut()
  }

  const isDifferentTimezone = location && hasTimeOffsetFromUser(location.timeZone)

  return (
    <>
      {!kiosk && <LocationSwitcher open={showLocationSwitcher} onClose={handleLocationClose} />}

      <Box className={classes.tray}>
        {!kiosk && (
          <Hidden mdDown>
            {locations && locations.length > 0 && (
              <Box className={classes.trayItem} onClick={global ? null : handleLocationClick}>
                {!global && location && (
                  <Chip
                    classes={{
                      root: `${classes.trayOrganisation} ${classes.clickable}`,
                      label: classes.trayOrganisationLabel,
                    }}
                    label={
                      <Hidden smDown>
                        {truncateInMiddle(location.name, 30)}
                        {isDifferentTimezone && (
                          <small className={classes.time}>{moment.tz(location.timeZone).format("z")}</small>
                        )}
                      </Hidden>
                    }
                    avatar={
                      organisation.logo && <Avatar avatar={{ key: organisation.logo }} fullName={organisation.name} />
                    }
                  />
                )}
                {!global && !location && (
                  <>
                    <ErrorIcon color="error" title="There are no locations associated with your user." />
                    <Chip
                      classes={{ root: classes.trayOrganisation, label: classes.trayOrganisationLabel }}
                      label={<Hidden smDown>{organisation.name}</Hidden>}
                      avatar={
                        organisation.logo && <Avatar avatar={{ key: organisation.logo }} fullName={organisation.name} />
                      }
                    />
                  </>
                )}
                {global && (
                  <Chip
                    classes={{ root: classes.trayOrganisation, label: classes.trayOrganisationLabel }}
                    label={<Hidden smDown>All locations</Hidden>}
                    avatar={
                      organisation.logo && <Avatar avatar={{ key: organisation.logo }} fullName={organisation.name} />
                    }
                  />
                )}
              </Box>
            )}
          </Hidden>
        )}
        {!kiosk && <AnnouncementButton className={classes.trayItem} buttonClassName={classes.trayIconButton} />}
        {!kiosk && !device && (
          <Box className={classes.trayItem}>
            <HelpMenu className={classes.trayIconButton} menuClasses={{ paper: classes.menu }} />
          </Box>
        )}
        <RequirePermissions requires={["post_read", "knowledge_read", "jobprocess_read_self", "jobprocess_read_all"]}>
          <Box className={classes.trayItem}>
            <IconButton
              ref={searchButtonEl}
              title="Search"
              aria-label="Search"
              onClick={handleSearchClick}
              className={classes.trayIconButton}
              data-cy="IconButton-search"
            >
              <Icon name="search" />
            </IconButton>
            <SearchPopover open={Boolean(searchEl)} anchorEl={searchEl} onClose={handleSearchClose} />
          </Box>
        </RequirePermissions>
        {hasFeature("calendar") && (
          <Box className={classes.trayItem}>
            <Tooltip title="Calendar">
              <IconButton
                component={Link}
                to="/calendar"
                className={classes.trayIconButton}
                data-cy="IconButton-calendar"
              >
                <Icon name="calendar" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        <Box className={classes.trayItem}>
          <NotificationIndicator buttonClassName={classes.trayIconButton} iconClassName={classes.trayNotifications} />
        </Box>
        <Box>
          <IconButton
            title="Your account"
            onClick={handleAccountClick}
            ref={accountButtonEl}
            className={classes.trayAvatarButton}
            data-cy="HeaderTray-IconButton-account"
          >
            <Avatar className={classes.trayAvatar} firstName={firstName} lastName={lastName} avatar={{ key: avatar }} />
          </IconButton>
          <Popover
            open={Boolean(accountEl)}
            anchorEl={accountEl}
            onClose={handleAccountClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: theme.spacing(1), horizontal: "right" }}
            classes={{ paper: classes.menu }}
          >
            <MenuItem className={classes.menuItem}>
              <Box py={1}>
                <Box className={classes.menuUser} mb={0.3}>
                  {firstName} {lastName}
                </Box>
                <Box className={classes.menuLight}>{external ? email : username}</Box>
              </Box>
            </MenuItem>
            {!kiosk && (
              <>
                <Divider />
                <MenuItem className={`${classes.menuItem} ${classes.menuItemShaded}`} onClick={handleLocationClick}>
                  <Box display="flex" flexGrow={1} flexDirection="column">
                    <Box className={classes.menuLight}>Current location</Box>
                    <Box display="flex" flexDirection="row" flexGrow={1} alignItems="center">
                      {location && (
                        <>
                          {organisation.logo && (
                            <ListItemIcon className={classes.menuListItemIcon}>
                              <Avatar
                                avatar={{ key: organisation.logo }}
                                fullName={organisation.name}
                                className={classes.organisationLogo}
                              />
                            </ListItemIcon>
                          )}
                          <ListItemText
                            classes={{ primary: classes.locationPrimary, secondary: classes.locationSecondary }}
                            primary={truncateInMiddle(location.name, xs ? 28 : 50)}
                            secondary={isDifferentTimezone && <small>{moment.tz(location.timeZone).format("z")}</small>}
                          />
                          <Box display="flex" justifySelf="flex-end" alignItems="center">
                            <SwapHoriz />
                          </Box>
                        </>
                      )}
                      {!location && (
                        <>
                          <ListItemIcon className={classes.menuListItemIcon}>
                            <ErrorIcon color="error" />
                          </ListItemIcon>
                          <ListItemText primary="No locations" secondary="Contact your administrator" />
                        </>
                      )}
                    </Box>
                  </Box>
                </MenuItem>
              </>
            )}
            <Divider />
            <MenuItem
              component={NavLink}
              to="/account"
              className={classes.menuItem}
              onClick={() => setAccountEl(null)}
              data-cy="HeaderTray-MenuItem-settings"
            >
              <ListItemIcon className={classes.menuListItemIcon}>
                <TuneIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </MenuItem>
            {!kiosk && (
              <>
                <Divider />
                <MenuItem className={classes.menuItem} onClick={handleLogout} data-cy="MenuItem-logout">
                  <ListItemIcon className={classes.menuListItemIcon}>
                    <ExitToAppIcon />
                  </ListItemIcon>
                  <ListItemText primary="Sign out" />
                </MenuItem>
              </>
            )}
          </Popover>
        </Box>
        {kiosk && (
          <RowBox ml={3}>
            <Box className={classes.kioskTimeLeft}>
              <IdleTimer onLoggingOut={handleIdleLoggingOut} />
            </Box>
            <Button variant="contained" color="secondary" onClick={handleKioskLogout} className={classes.kioskLogout}>
              Logout
            </Button>
          </RowBox>
        )}
      </Box>
    </>
  )
}

export default HeaderTray
